/**
 * EstateVaultUpgradeLock — shown on the Continuity System tab for users on the
 * Portfolio plan. A calm, benefit-first preview of what the Continuity plan
 * unlocks, with a single "Upgrade" call to action.
 *
 * When Phase D (Pricing page) ships, the CTA will route to Stripe checkout.
 * For now (beta / test), it calls the existing tier-choice endpoint so the
 * founder and test users can flip themselves without going through billing.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Shield, Users, ClipboardList, FileLock2, Sparkles, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import UpgradeConfirmationModal from '../subscription/UpgradeConfirmationModal';
import logger from '../../utils/logger';
import { useUserTier } from '../../hooks/useUserTier';
import { useQualifyingCriteria } from '../../hooks/useQualifyingCriteria';
import { VaultProgressPanel } from './VaultProgressPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UNLOCKS = [
  { key: 'assets', icon: ClipboardList, title: 'Your Assets, in plain English', body: 'For every investment, capture what it is, where it is held, how much, and where the paperwork lives.' },
  { key: 'contacts', icon: Users, title: 'Trusted Contacts', body: 'Name the people who should hear from us if you go quiet \u2014 with the share each one receives.' },
  { key: 'documents', icon: FileLock2, title: 'Encrypted Documents', body: 'Attach up to three private documents per asset. Only your Trusted Contacts can ever unlock them, and only after our quiet check-in cycle confirms.' },
  { key: 'checkin', icon: Shield, title: 'Quiet Check-in Cycle', body: 'Every few months we ask "are you well?". If you do not answer for a long time, and only after two of our team confirm, your Trusted Contacts receive what you have prepared.' },
];

export function EstateVaultUpgradeLock({ onUpgraded }) {
  const [showModal, setShowModal] = useState(false);
  const [pricingReady, setPricingReady] = useState(false);
  const { isGatewayContinuity } = useUserTier();
  const { data: criteriaData } = useQualifyingCriteria(isGatewayContinuity);
  const counts = criteriaData?.counts || {};

  // Pre-warm the pricing endpoint so the modal opens without a spinner delay.
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    fetch(`${BACKEND_URL}/api/pricing/v3`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then((r) => { if (r.ok) setPricingReady(true); })
      .catch((err) => logger.error('prewarm pricing/v3', err));
  }, []);

  return (
    <div
      className="bg-white rounded-xl shadow-xs border border-slate-200 p-6 sm:p-8"
      data-testid="estate-vault-upgrade-lock"
    >
      <div className="flex items-start gap-3 mb-6">
        <div className="shrink-0 bg-emerald-50 border border-emerald-100 rounded-lg p-2">
          <Sparkles className="w-5 h-5 text-emerald-700" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-lg sm:text-xl font-serif font-semibold text-slate-900" data-testid="upgrade-lock-heading">
            {isGatewayContinuity ? 'Your Continuity System vault' : 'The Continuity System comes with Continuity System'}
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            {isGatewayContinuity ? (
              <>Welcome to Continuity System &mdash; a quiet, private way to make sure the people you trust would know what to do if life takes an unexpected turn. Here&rsquo;s what you can set up:</>
            ) : (
              <>You&rsquo;re currently on <strong>Portfolio Clarity</strong>. Continuity System is our second plan &mdash; a quiet, private way to make sure the people you trust would know what to do if life takes an unexpected turn.</>
            )}
          </p>
          <p className="mt-1.5 text-xs italic text-emerald-800/80 flex items-center gap-1.5" data-testid="vault-encryption-trust-line">
            <Lock className="w-3 h-3" aria-hidden="true" />
            Encrypted at rest. Even we can&rsquo;t read your entries.
          </p>
        </div>
      </div>

      {isGatewayContinuity && <VaultProgressPanel />}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {UNLOCKS.map(({ key, icon: Icon, title, body }) => {
          const count = counts[key === 'assets' ? 'assets' : key === 'contacts' ? 'trusted_contacts' : key === 'documents' ? 'documents' : null];
          const showCount = isGatewayContinuity && typeof count === 'number' && key !== 'checkin';
          return (
            <div key={title} className="border border-slate-200 rounded-lg p-4 bg-slate-50/60" data-testid={`vault-card-${key}`}>
              <div className="flex items-center justify-between mb-1.5 gap-2">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-emerald-700" aria-hidden="true" />
                  <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
                </div>
                {showCount && (
                  <span
                    className={`text-[11px] font-mono font-semibold px-1.5 py-0.5 rounded-full ${count > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}
                    data-testid={`vault-card-count-${key}`}
                  >
                    {count} {count === 1 ? 'entry' : 'entries'}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
            </div>
          );
        })}
      </div>

      <div className="border-t border-slate-100 pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-slate-500 max-w-lg">
          {isGatewayContinuity
            ? 'The Vault is a separate, encrypted space — you fill it in at your own pace, and no one else sees any of it unless our check-in cycle is triggered.'
            : 'Nothing changes for your portfolio view. The Vault is a separate, encrypted space — you fill it in at your own pace, and no one else sees any of it unless our check-in cycle is triggered.'}
        </p>
        {!isGatewayContinuity && (
          <Button
            onClick={() => setShowModal(true)}
            disabled={!pricingReady}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="upgrade-lock-cta"
          >
            {pricingReady ? (
              <>Upgrade to Continuity</>
            ) : (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
            )}
          </Button>
        )}
      </div>

      {showModal && (
        <UpgradeConfirmationModal
          pricing={null}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); if (onUpgraded) onUpgraded(); }}
        />
      )}
    </div>
  );
}

export default EstateVaultUpgradeLock;
